import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { PubSubService, declareEvent } from './pubsub.service';
import { AuthService } from './auth.service';
import { CouchLoginReason } from './couch-login-storm';
import type { LoggingService } from './logging.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { getUnversionedRelayOrigin } from 'util/backend-origin';
import { fetchReachableAsync, pingWithinProbeLimitAsync, ProbeRunningTimeLimitError, REACHABILITY_PROBE_TIMEOUT_MS } from './couch-login-online-gate';
import { ComingOnlineLoginFailure, decideComingOnlineLoginFailure } from './coming-online-login-failure';
import { DeferredPromise } from '@cf-shared/shared-util';
import { awaitFirstProbeGateAsync } from './first-probe-gate';
import { runningTimeTimer } from './local-store-timeout';
import { decideRefusalRecheck, RefusalRecheckDecision } from './refusal-recheck-gate';
import { ProbeResultGate } from './probe-result-gate';
import { ConnectionStatePublishGate } from './connection-state-publish-gate';
import { registerSentryConnectionStateProvider } from 'app/app-sentry';

export enum ConnectionState {
  Offline = 'offline',
  Online = 'online'
}

// F11 (#3988): how long assureInitialSyncAsync waits for the first reachability
// probe before giving up and reading whatever connectionState currently holds.
// It must EXCEED the probe's own timeout (REACHABILITY_PROBE_TIMEOUT_MS) so a
// slow-but-online first ping is awaited to completion instead of being cut short
// mid-probe — otherwise the gate would time out while the state is still the
// boot Offline placeholder, the exact race F11 closes. The extra margin also
// covers the state-publish that runs after the probe resolves. Because the probe
// self-bounds at REACHABILITY_PROBE_TIMEOUT_MS, this ceiling normally only acts
// as a backstop for a probe that never runs at all. It is NOT sized for the
// CouchDB login refresh that an Offline -> Online flip also waits for, so a slow
// login can outlast it with the verdict already written; the gate then records
// info, not a problem (#6121). It counts running time, not wall-clock time; the
// probe's own limit counts running time too (#6389), so the margin also holds after
// the phone froze the app.
export const FIRST_PROBE_GATE_TIMEOUT_MS = REACHABILITY_PROBE_TIMEOUT_MS + 2000;

export const connectionStateChangedEvent = declareEvent<ConnectionState>('connectionStateChanged');

// We cannot trust navigator.onLine reliably, especially on mobile devices.
// This service monitors the online/offline state of the app and checks that it is correct, by pinging our server.
// Other systems cooperate by calling verifyOnlineStatus in additional cases, where it might be needed to verify the state.
@Injectable({ providedIn: 'root' })
export class OnlineStateService {
  private _connectionState: ConnectionState = ConnectionState.Offline;
  // bound to `window` — see the note in app.component.ts: a detached `fetch`
  // reference is not callable with an arbitrary `this`.
  private _fetch: typeof fetch = window.fetch.bind(window);
  private authService: AuthService;
  private loggingService: LoggingService;
  // F11 (#3988): resolves once the first startup reachability probe has run, so
  // callers (assureInitialSyncAsync) don't read the Offline placeholder above
  // before the probe flips it. Named `when*` (like whenAppReady / whenFirebase
  // AuthSettled) to signal it is a promise, not a boolean flag. DeferredPromise.
  // resolve is idempotent — later probes calling it again are harmless no-ops.
  private whenFirstProbeDone = DeferredPromise.defer<void>();
  /** #3991: when the last probe actually completed. Lets a refusal trust a recent verdict instead of
   *  re-probing (see isLikelyOnlineOrRecheckAsync + RECENT_PROBE_TRUST_MS). */
  private lastProbeAtMs = 0;
  /** df-dfa0b79fc17e: nothing here coordinates the probes, and several things start one. This
   *  puts them in order, so a probe that has been overtaken by a newer one drops its answer
   *  instead of overwriting fresher news with it. See probe-result-gate.ts. */
  private probeResultGate = new ProbeResultGate();
  /** df-c281f1fe39ea: numbers every write to _connectionState, so an announcement made long after
   *  its write (a flip to Online waits for the CouchDB login first) is dropped once a newer write
   *  has happened. See connection-state-publish-gate.ts. */
  private publishGate = new ConnectionStatePublishGate();

  constructor(
    private pubSubService: PubSubService
  ) {
    // #3992: let the module-level Sentry setup tag every event with the
    // current connection state (it cannot inject this service itself)
    registerSentryConnectionStateProvider(() => this._connectionState);

    // online event is not reliable according to https://html.spec.whatwg.org/dev/system-state.html#navigator.online
    window.addEventListener('online', () => this.verifyOnlineStatus(ConnectionState.Online, 'suspected'));
    // offline event should be reliable, we will report a problem if this is not the case
    window.addEventListener('offline', () => this.verifyOnlineStatus(ConnectionState.Offline, 'expected'));

    // verify the online state on startup (use 'suspected' because the app's server may not be reachable yet during bootstrap)
    // eslint-disable-next-line no-restricted-syntax -- OnlineStateService is the one allowed navigator.onLine reader (F9)
    void this.verifyOnlineStatus(navigator.onLine ? ConnectionState.Online : ConnectionState.Offline, 'suspected');
  }

  /**
   * The one signal feature code should use for "can I reach our server right now?" gates.
   *
   * Why not read navigator.onLine directly: the browser flag is only trustworthy in one
   * direction. `navigator.onLine === false` really does mean "definitely offline", but
   * `true` only means "there is some network interface up" — it says nothing about whether
   * our server is actually reachable (captive portals, airplane-mode edge cases, a phone on
   * a wifi network with no internet behind it all report true). So we trust the flag only
   * when it says false, and otherwise defer to the last verified probe of our own server.
   *
   * Do NOT read navigator.onLine directly in feature code — a lint rule enforces this.
   */
  isLikelyOnline(): boolean {
    // eslint-disable-next-line no-restricted-syntax -- OnlineStateService is the one allowed navigator.onLine reader (F9)
    return navigator.onLine !== false && this._connectionState === ConnectionState.Online;
  }

  /**
   * Does the browser itself say there is no network at all? The one direction its flag can be
   * trusted (see `isLikelyOnline`). Unlike `isLikelyOnline`, this needs no probe of our server, so
   * it is the signal for a decision taken at boot before any probe has run (the warm-resume check,
   * df-e30dd9684cf6).
   */
  browserSaysDefinitelyOffline(): boolean {
    // eslint-disable-next-line no-restricted-syntax -- "definitely offline" is the one direction the raw flag can be trusted (F9)
    return navigator.onLine === false;
  }

  /**
   * The signal to use when a feature is about to REFUSE the user because it thinks we are offline.
   *
   * Why `isLikelyOnline()` alone is not good enough for that: `_connectionState` is refreshed only
   * by the window online/offline events, a focus/visibility change, a failed request elsewhere, the
   * one 2-second retry after a failed "expected online" ping, and — since df-eabc7656368c — the
   * six-minute foreground tick, which re-probes while the state says offline. So a single failed
   * probe — a server hiccup, a captive portal, a cold-boot ping that timed out — can still leave the
   * state stuck on Offline for minutes on a device that is foregrounded and working perfectly well.
   * A cheap read is fine for a passive indicator (the pill is allowed to be briefly wrong), but it
   * is NOT fine for a gate that tells the user "you can't do this" — that would be a regression:
   * before this signal existed these paths read navigator.onLine, saw `true`, and simply worked.
   *
   * The decision itself is the pure, table-tested `decideRefusalRecheck` — it is far too easy to get
   * wrong in either direction to leave as an if-chain here.
   */
  async isLikelyOnlineOrRecheckAsync(): Promise<boolean> {
    const decision = decideRefusalRecheck({
      isLikelyOnline: this.isLikelyOnline(),
      // eslint-disable-next-line no-restricted-syntax -- "definitely offline" is the one direction the raw flag can be trusted (F9)
      rawFlagSaysDefinitelyOffline: navigator.onLine === false,
      // never probed yet (boot) reads as infinitely old, so we go and probe rather than trust a
      // verdict that does not exist
      msSinceLastProbe: this.msSinceLastProbe(),
    });

    if (decision === RefusalRecheckDecision.Allow) return true;
    if (decision === RefusalRecheckDecision.Refuse) return false;

    // Recheck: the browser thinks there is a network, our state says offline, and that verdict is old
    // enough to be stale. Verify before telling the user they cannot do something.
    //
    // verifyOnlineStatus, NOT probeServerReachableAsync + forceOnline: on an Offline->Online flip it
    // also refreshes the CouchDB session (setConnectionStateAsync's auth-refresh branch). forceOnline
    // deliberately SKIPS that refresh, so healing with it would restart sync against a session cookie
    // that may have gone stale during the (falsely) offline stretch — straight into 401s that
    // handleSyncAuthError retries only three times before giving up with a user-visible error.
    console.log('ConnectionStatus: refusal re-check — cached Offline state is stale, re-verifying');
    const verified = await this.verifyOnlineStatus(ConnectionState.Online, 'suspected');
    return verified === ConnectionState.Online;
  }

  /** S7 (#4027 Step 25): age of the last completed probe verdict — Infinity before the first
   *  probe. This is the AGE only; neither consumer trusts it on its own.
   *
   *  The refusal-recheck decision above pairs it with what we believe and what the browser flag
   *  says (decideRefusalRecheck). The automatic focus-resume check pairs it with what we believe
   *  and skips a redundant HEAD probe only when that belief is Online — see shouldSkipResumeProbe
   *  in resume-probe-gate.ts, which explains why an Offline belief is never reused at any age
   *  (#4318). Skipping on age alone is what left a resumed Android app stuck showing "Offline". */
  msSinceLastProbe(): number {
    return this.lastProbeAtMs === 0 ? Infinity : Date.now() - this.lastProbeAtMs;
  }

  // Set the auth service to avoid circular dependency issues
  setAuthService(authService: AuthService) {
    this.authService = authService;
  }

  // Set the logging service to avoid circular dependency issues
  setLoggingService(loggingService: LoggingService) {
    this.loggingService = loggingService;
  }

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  // a method, not a property read, on purpose: a caller that already checked `connectionState`
  // before an await gets the earlier answer narrowed in by TypeScript, and this re-reads the
  // live state (#6231, attemptCouchLogin_innerAsync's re-probe gate)
  isConnectionStateOnline(): boolean {
    return this._connectionState === ConnectionState.Online;
  }

  /**
   * Force connection state to Online without triggering auth refresh.
   * Called by AuthService after couchLogin succeeds on retry while status is still Offline.
   * Bypasses setConnectionStateAsync to avoid circular dependency (auth → online → auth).
   */
  forceOnline() {
    if (this._connectionState === ConnectionState.Online) return;
    this._connectionState = ConnectionState.Online;
    this.publishGate.recordWrite();
    console.log('ConnectionStatus: Online');
    this.pubSubService.publish(connectionStateChangedEvent, ConnectionState.Online);
  }

  // #3878: the connectivity probe address (HEAD target). On Capacitor native a relative path
  // resolves to a local file (always succeeds regardless of network), so ping the real server.
  //
  // #4405: the relay accessor, not the waiting one. This probe exists to answer
  // "is there a network at all?", and it fetches a static image to find out — it
  // sends no credentials and changes nothing, so either server answers it the
  // same way. Waiting for the backend route here would be circular in the one
  // case that matters: on an offline first-ever boot the route cannot resolve
  // BECAUSE there is no network, and a probe that waited for it could never
  // report the very offline state it was asked about. That was the old frozen
  // screen, and it is exactly what this ticket removes.
  private get pingAddress(): string {
    return isCapacitorNative() ? `${getUnversionedRelayOrigin()}/assets/imgs/ping.svg` : `/assets/imgs/ping.svg`;
  }

  // #3878: a one-shot reachability probe that does NOT mutate connection state. Safe to call
  // from inside attemptCouchLoginAsync — unlike verifyOnlineStatus(), which flips state via
  // setConnectionStateAsync() and would re-enter attemptCouchLoginAsync and deadlock on the
  // in-flight login. Returns true if the server answers at all (even non-2xx). `timeoutMs` lets a
  // caller that has already waited on something else ask with a shorter limit (the food search's
  // stalled-request check, df-bac7bf9dc8fa).
  public async probeServerReachableAsync(timeoutMs: number = REACHABILITY_PROBE_TIMEOUT_MS): Promise<boolean> {
    return fetchReachableAsync(this._fetch, this.pingAddress, timeoutMs);
  }

  private async setConnectionStateAsync(newState: ConnectionState) {

    // if the state is not changed, do nothing
    if (this._connectionState === newState) {
      return;
    }

    const previousState = this._connectionState;
    this._connectionState = newState;
    let writeNumber = this.publishGate.recordWrite();

    // log the connection state change
    if (newState === ConnectionState.Online) {
      console.log('ConnectionStatus: Online');
    } else {
      console.log('ConnectionStatus: Offline');
    }

    // When coming back online, refresh couchAuth before notifying subscribers
    if (previousState === ConnectionState.Offline && newState === ConnectionState.Online && this.authService?.user) {
      console.log('ConnectionStatus: Refreshing couch auth after coming online...');
      try {
        // warm-resume fast path synthesizes `authService.user` from cache before
        // Firebase has restored its session (issue #3184). Couch login requires
        // a real Firebase user — wait for that to be ready before attempting it,
        // otherwise attemptCouchLoginAsync bails with "No Firebase user" and we
        // latch into offline for the whole session.
        await this.authService.firebaseReadyAsync('online-flip');
        const authSuccess = await this.authService.attemptCouchLoginAsync(CouchLoginReason.OnlineRegained);
        if (!authSuccess) {
          throw new Error('CouchDB authentication failed');
        }
        console.log('ConnectionStatus: Couch auth refreshed successfully');
      } catch (error) {
        // snapshot the classification of THIS attempt's failure BEFORE any await below:
        // a concurrent focus/online retry (retryCouchLoginAsync) can start a new attempt
        // during the verifyOnlineStatus await, and that new attempt resets authService's
        // stored error — re-reading it after the await could misclassify this failure
        const wasRecoverableFailure = this.authService.isLastCouchLoginErrorRecoverable();
        const wasVersionMismatch = this.authService.isLastCouchLoginErrorVersionMismatch();
        const wasOsTimeout = this.authService.isLastCouchLoginErrorOsTimeout();
        const failureReport = this.authService.getLastCouchLoginErrorReport();
        const skipReason = this.authService.getLastCouchLoginSkipReason();
        // a weird case is possible here - we went online briefly, but dropped offline again before we could refresh auth
        // in that case, we should not report an error, but just go back to offline state
        let actualState = await this.verifyOnlineStatus(ConnectionState.Offline, 'suspected');
        if (actualState === ConnectionState.Offline) {
          console.log('ConnectionStatus: Went offline again before auth could be refreshed');
          return;
        }
        const outcome = decideComingOnlineLoginFailure({
          signOutRace: this.authService.isLikelySignOutRace(),
          versionMismatch: wasVersionMismatch,
          recoverableError: wasRecoverableFailure,
          timedOut: wasOsTimeout,
          skipReason,
        });
        switch (outcome) {
          case ComingOnlineLoginFailure.SignOutRace:
            // sign-out race: user signed out (or Firebase token revoked) while we awaited
            // firebaseReadyAsync + attemptCouchLoginAsync — couch login was never going to
            // succeed in that case. Demote to console.log, stay offline.
            console.log('ConnectionStatus: Couch auth skipped — user signed out during refresh (benign sign-out race)');
            break;
          case ComingOnlineLoginFailure.VersionMismatch:
            // #4283: this server is running an older _users doc format version than this
            // account's data — an expected, recoverable condition (it resolves once the
            // live server catches up), not a real auth problem. Deliberately NOT reported
            // via trackSilentProblem here, matching auth.service.ts's own choice for this
            // same condition (see its comment on the isVersionMismatchCouchLoginError
            // branch): the focus/online retry listeners fire on every flip, so reporting
            // here would be unbounded Sentry-adjacent volume (one event per user per flip)
            // for the whole duration of a live incident. Observability already exists via
            // the couch_login_failure Mixpanel event's distinct ServerDataVersionMismatch
            // error_name, fired once per couchLogin attempt from auth.service.ts.
            console.log('ConnectionStatus: Couch auth failed — server is behind this account\'s data version — staying offline, auto-retry will recover', failureReport);
            break;
          case ComingOnlineLoginFailure.NetworkFailure:
            // #4010 hygiene: the login failed at the NETWORK layer (DNS failure, dropped
            // connection, timeout), not because the server rejected our credentials. The
            // ping probe above can still pass — mobile flaps recover within seconds, and
            // some requests succeed while others die (#4014's proven case). Reporting this
            // as "CouchDB authentication failed" mislabeled genuine network trouble as an
            // auth problem (Sentry APP-3Z9). Keep it measurable under an accurate name,
            // stay offline, and let the focus/online retry listeners recover.
            console.log('ConnectionStatus: Couch auth failed with a network-shaped error while coming online — staying offline, auto-retry will recover', failureReport);
            // #6231: also reached when the login bailed OfflineAtStart — its own reachability
            // re-probe failed, so it never asked the server; failureReport.skip_reason says so
            this.loggingService?.trackSilentProblem('CouchAuthNetworkFailureWhileOnline', failureReport);
            break;
          case ComingOnlineLoginFailure.FirebaseNotReady:
            // #6231: Firebase had not restored the user, so the login never asked the server, and
            // the user is back by now (else this would be a sign-out race). Nothing refused us, so
            // no user message; the slow restore itself is reported as BootAuthStateReadyTimeout.
            // Stay offline and let the retry listeners recover, as for a network failure.
            console.log('ConnectionStatus: Couch auth skipped — Firebase had not restored the user yet — staying offline, auto-retry will recover', failureReport);
            this.loggingService?.trackSilentProblem('CouchAuthFirebaseNotReadyWhileOnline', failureReport);
            break;
          case ComingOnlineLoginFailure.LoginTimedOut:
            // df-97e69e66c032: every retry hit the iOS system timeout ("The request timed out.").
            // Nothing refused us, so no auth-failed message. No report either: the retry loop has
            // already filed it as "Auth: Couch login - too many retries" with this message, which
            // is what keeps a slow server visible. Stay offline and let the retry listeners recover.
            console.log('ConnectionStatus: Couch auth timed out on every retry while coming online — staying offline, auto-retry will recover', failureReport);
            break;
          case ComingOnlineLoginFailure.AuthFailure:
            // real auth failure — stay offline and surface to Sentry. #6231: trackAbnormalError
            // takes no context, so log the cause first; it lands on the event as a breadcrumb.
            // The title stays fixed so Sentry keeps grouping these as one issue.
            console.log('ConnectionStatus: Couch auth failed when coming online', failureReport);
            if (this.loggingService) {
              this.loggingService.trackAbnormalError('CouchAuth failed when coming online: ' + error.message, LL.errors.userFacing.authFailed());
            } else {
              console.error('ConnectionStatus: LoggingService not available to report auth failure');
            }
            break;
          default: {
            // the compiler flags this line when ComingOnlineLoginFailure gains a value with no case
            const unhandled: never = outcome;
            this.loggingService?.trackSilentProblem('ComingOnlineLoginFailureUnhandled', { outcome: String(unhandled) });
          }
        }
        // df-dfa0b79fc17e deliberately does NOT gate this write on the probe order, and a future
        // reader should not "fix" that. This is not a stale ping answer arriving late: it is the
        // freshly-decided outcome of trying to log in, and it holds whatever any ping says — we
        // cannot claim to be online with a session we could not refresh. Gating it would also drop
        // it every single time, because the verifyOnlineStatus call a few lines up is itself a
        // newer probe and always answers after this one wrote.
        this._connectionState = ConnectionState.Offline;
        writeNumber = this.publishGate.recordWrite();
        newState = ConnectionState.Offline;
      }
    }

    // df-c281f1fe39ea: if something wrote the state while we waited for the login above, it has
    // announced (or will announce) its own value — announcing ours now would leave subscribers on
    // older news than the state itself holds
    if (!this.publishGate.isLatestWrite(writeNumber)) {
      console.log(`ConnectionStatus: not announcing ${newState}; the state was changed again while this change waited, and that newer change announces itself`);
      return;
    }

    // notify subscribers about the state change
    this.pubSubService.publish(connectionStateChangedEvent, newState);
  }

  // Set a custom fetch function to use for pinging the server. This prevents infinite loops if the fetch fails causing the online state to change.
  setFetch(fetchFunction: typeof fetch) {
    this._fetch = fetchFunction;
  }

  /**
   * Update the online status of the app by verifying the input assumption.
   *
   * The reason we do this is that the online/offline events are not always reliable, especially on mobile.
   * So we call here if we have any indication that the app might be on/offline - e.g., if the navigator.onLine property changes,
   * but also if a network request fails. This verifies by trying a request to our server.
   *
   * If the input confidence is 'expected', it will report an error if the actual state is inconsistent with the expected state.
   * This is mostly in cases where, e.g., some other fetch fails, but the ping passes.
   *
   * Several things call this and none of them know about each other, so two checks can be in
   * flight at once. A check that is overtaken while it runs — one that started later has already
   * published its answer — throws its own answer away rather than publishing older news over
   * newer, and reports the state that actually holds instead (df-dfa0b79fc17e, ProbeResultGate).
   *
   * @param expectedState - The expected connection state ('online' or 'offline').
   * @param confidence - The confidence level ('expected' or 'suspected').
   * @returns The connection state: what this check found, or — if it was overtaken — what the
   *          newer check that beat it to the answer published.
   */
  async verifyOnlineStatus(expectedState: ConnectionState, confidence: 'expected' | 'suspected'): Promise<ConnectionState> {
    // df-dfa0b79fc17e: take this probe's place in the order BEFORE the ping starts, so that when it
    // answers we can tell whether a probe that started later has already answered ahead of us.
    const probeNumber = this.probeResultGate.startProbe();

    // we will remember the error message, but will report it only if the state is unexpectedly inconsistent
    let error = null;

    // ping our own server and see if it responds
    // on Capacitor native, the relative path would resolve to a local file (capacitor://localhost/...)
    // which always succeeds regardless of network state — so we ping the actual server instead
    const pingAddress = this.pingAddress;
    let actualState: ConnectionState;
    try {
      // L3 (#3986): bound the ping so a black-hole network can't hang verifyOnlineStatus
      // forever. A timed-out ping throws into the catch below and we settle on Offline — the
      // same outcome as any other ping failure. #6389: bounded on running time as well, so
      // after a freeze the first check still answers before the first-probe gate gives up.
      let result = await pingWithinProbeLimitAsync(this._fetch, pingAddress);
      // ping succeeded — server is reachable, we're online
      // note: we consider !result.ok as online, because it means the server is reachable, but the resource is not found
      actualState = ConnectionState.Online;
      error = result.statusText || `HTTP status ${result.status}`;
    } catch (e) {
      actualState = ConnectionState.Offline;
      error = e.message || 'Unknown network error';
      // #6389: the running-time limit ended this check, which a freeze usually causes. Info,
      // not a problem: the check has already ended and the app carries on. signalAborted says
      // whether the request had also ignored its abort signal (a fetch that does not end on
      // abort); notRunningMs how long the page was frozen meanwhile.
      if (e instanceof ProbeRunningTimeLimitError) {
        this.loggingService?.trackSilentInfo('OnlineState: reachability check ended by its running-time limit', {
          signalAborted: e.signalAborted,
          notRunningMs: e.notRunningMs,
        });
      }
    }

    // if we expected online but ping failed, schedule a background retry
    // (on iOS, first request after background suspension often fails while network reconnects)
    if (actualState === ConnectionState.Offline && expectedState === ConnectionState.Online) {
      console.log(`ConnectionStatus: Online ping failed (${error}), scheduling background retry...`);
      this.scheduleBackgroundPingRetry(pingAddress);
    }

    // if the actual state is inconsistent with the expected state, report an error if needed
    if (actualState !== expectedState && confidence === 'expected') {
      console.error(`Expected ${expectedState}, but got ${actualState}. Error: ${error}`);
    }

    try {
      // df-dfa0b79fc17e: a probe that started after this one has already published its answer, so
      // what we are holding is out of date — drop it rather than overwrite fresher news with it.
      // We report the state that actually holds, because every caller uses what this returns as
      // "what is the connection state?", and the newest answer is the honest one to give them.
      if (!this.probeResultGate.claimResult(probeNumber)) {
        console.log(`ConnectionStatus: dropping an overtaken connectivity check (it found ${actualState}); a newer check has already answered`);
        return this._connectionState;
      }

      // record WHEN this verdict was reached, so a later refusal can decide whether to trust it
      this.lastProbeAtMs = Date.now();

      await this.setConnectionStateAsync(actualState);
    } finally {
      // F11: mark the first probe complete so awaitFirstProbeAsync callers can
      // stop waiting. In a finally so a throw inside setConnectionStateAsync
      // (e.g. a subscriber throwing during publish) still releases them rather
      // than making them wait the full ceiling. Idempotent — only the first
      // call matters. The state was already assigned inside setConnectionStateAsync
      // before any throwable publish, so callers still read the fresh value.
      this.whenFirstProbeDone.resolve();
    }
    return actualState;
  }

  /** Resolves once the first startup reachability probe has completed (either
   * outcome). Bounded: never waits longer than timeoutMs of running time, so a
   * hung probe cannot stall callers. Used by assureInitialSyncAsync so a cold boot
   * does not read the constructor's Offline placeholder before the probe ran.
   * Reports a problem only when no check has written a verdict by then (#6121). */
  async awaitFirstProbeAsync(timeoutMs: number): Promise<void> {
    await awaitFirstProbeGateAsync({
      // whenFirstProbeDone is a DeferredPromise, which IS a Promise (it extends
      // it), so pass it straight into the race — there is no inner .promise.
      firstProbeDone: this.whenFirstProbeDone,
      timeoutMs,
      // #6121: running time, so a phone freezing the app in the background does not use
      // the limit up before the check has had any time to answer
      timer: runningTimeTimer(),
      msSinceVerdict: () => this.msSinceLastProbe(),
      connectionState: () => this._connectionState,
      reportProblem: (name, context) => this.loggingService?.trackSilentProblem(name, context),
      reportInfo: (name, context) => this.loggingService?.trackSilentInfo(name, context),
    });
  }

  /** Retry the online ping in the background after a short delay.
   * If the retry succeeds, transitions to Online (which triggers auth refresh + sync restart).
   * Doesn't block the caller — runs as fire-and-forget. */
  private scheduleBackgroundPingRetry(pingAddress: string) {
    setTimeout(async () => {
      // df-dfa0b79fc17e: this retry is a connectivity check like any other, so it takes its own
      // place in the order — at the moment its ping starts, not when the retry was scheduled two
      // seconds earlier. (It deliberately does not stamp lastProbeAtMs; it never has, and the
      // freshness window that reads that stamp is not what this change is about.)
      const probeNumber = this.probeResultGate.startProbe();
      // reuse the shared reachability probe (true if the server answers, false if the fetch throws)
      if (await fetchReachableAsync(this._fetch, pingAddress)) {
        if (!this.probeResultGate.claimResult(probeNumber)) {
          console.log('ConnectionStatus: Background ping retry succeeded, but a newer check has already answered — dropping this result');
          return;
        }
        console.log('ConnectionStatus: Background ping retry succeeded');
        await this.setConnectionStateAsync(ConnectionState.Online);
      } else {
        console.log('ConnectionStatus: Background ping retry also failed — staying offline');
      }
    }, 2000);
  }
}
